import { ReactNode, useEffect, useState } from "react";
import { v4 as uuidv4 } from 'uuid';

interface AnimatedTextProps {
    message: string;
    textClass: string;
    animatedTextRefs: AnimatedTextRefs;
    onTextUpdate?: () => void;
}

interface AnimatedTextRefs {
    animate: boolean;
    creationIndex: number;
}

// Function to detect and convert URLs to clickable links
const linkifyText = (text: string): ReactNode[] => {
    const urlRegex = /(https?:\/\/[^\s]+|file:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
        if (part.match(urlRegex)) {
            return (
                <a
                    key={index}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-chat-link underline hover:text-chat-link/80"
                >
                    {part}
                </a>
            );
        }
        return part;
    });
};

export function getAnimatedText(text: string, animate: boolean, textClass?: string, onTextUpdate?: () => void): ReactNode {
    const animateRef = { animate, creationIndex: 0 } as AnimatedTextRefs;
    const node: ReactNode = <AnimatedText
        message={text}
        textClass={textClass || ""}
        animatedTextRefs={animateRef}
        onTextUpdate={onTextUpdate}
        key={uuidv4()}
    />;
    return node;
};

export default function AnimatedText(props: AnimatedTextProps) {
    const [animateMessage, setAnimateMessage] = useState("");

    useEffect(() => {
        props.animatedTextRefs.creationIndex += 1;
        if (props.animatedTextRefs.creationIndex > 1) {
            props.animatedTextRefs.animate = false;
            return;
        }

        if (props.message === "showSpinner") {
            printChat("Thinking...");
        } else {
            printChat(props.message);
        }
    }, []);

    const printChat = (inputText: string) => {
        let i = 0;
        let message: string = inputText;
        const intervalId = setInterval(() => {
            setAnimateMessage(message.slice(0, i + 1));

            // Call the callback to trigger scroll update every 20 characters or at word boundaries
            if (props.onTextUpdate && (i % 20 === 0 || message[i] === ' ' || i === message.length - 1)) {
                props.onTextUpdate();
            }

            i++;
            if (i >= message.length) {
                clearInterval(intervalId);
                props.animatedTextRefs.animate = false;
            }
        }, 5);
    };

    const displayText = props.message === "showSpinner" ? "Thinking..." :
        props.animatedTextRefs.animate ? animateMessage : props.message;

    return (
        <span className={`px-1 w-11/12 block ${props.textClass}`}>
            {linkifyText(displayText)}
        </span>
    );
}